/**
 * CU-14zefhy2ydj C1: voice input with the browser's own speech recognition. This app never records, stores or uploads audio; the
 * text the browser returns is put into the question box for the user to read and send. Where the browser can recognise speech on
 * the device (`processLocally`) that is requested. No Angular imports, so it runs under node in the spec.
 */

/** The parts of the browser SpeechRecognition API used here. */
export interface SpeechRecognitionLike {
    lang: string;
    continuous: boolean;
    interimResults: boolean;
    processLocally?: boolean;
    onresult: ((event: any) => void) | null;
    onerror: ((event: any) => void) | null;
    onend: (() => void) | null;
    start(): void;
    stop(): void;
    abort(): void;
}

export type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

/** The browser's constructor (`SpeechRecognition` or the prefixed one), or null when it has none. */
export function speechRecognitionCtor(win: any): SpeechRecognitionCtor | null {
    return (win && (win.SpeechRecognition || win.webkitSpeechRecognition)) || null;
}

export function isVoiceSupported(win: any): boolean {
    return !!speechRecognitionCtor(win);
}

/** The recognition language: the browser's, else English (UK). */
export function voiceLang(nav: {language?: string} | null | undefined): string {
    const lang = nav && typeof nav.language === 'string' ? nav.language.trim() : '';
    return /^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(lang) ? lang : 'en-GB';
}

/**
 * The question box text while dictating: what was typed before (`base`), the words the browser is sure of, and the words it is
 * still guessing, joined with one space.
 */
export function mergeDictation(base: string, confirmed: string, interim: string): string {
    const spoken = [confirmed, interim]
        .map((part) => (part || '').trim())
        .filter((part) => !!part)
        .join(' ');
    const head = base || '';
    if (!spoken) {
        return head;
    }
    return head && !/\s$/.test(head) ? `${head} ${spoken}` : head + spoken;
}

/** A short message for a recognition error code, or '' when nothing needs saying (the user just stopped). */
export function voiceErrorText(code: string | null | undefined): string {
    switch (code) {
        case 'not-allowed':
            return 'Microphone access is blocked. Allow it for this site in the browser to dictate.';
        case 'service-not-allowed':
            return 'This browser does not allow speech recognition here. Type the question instead.';
        case 'audio-capture':
            return 'No microphone was found.';
        case 'network':
            return 'The browser could not reach its speech service. Type the question instead.';
        case 'language-not-supported':
            return 'This browser cannot recognise the language it is set to.';
        case 'no-speech':
            return 'No speech was heard.';
        case 'aborted':
        case undefined:
        case null:
        case '':
            return '';
        default:
            return `Voice input stopped (${code}). Type the question instead.`;
    }
}

/** Errors that an online retry cannot fix (the user stopped, or the microphone is the problem). */
const NO_ONLINE_RETRY = ['aborted', 'no-speech', 'not-allowed', 'audio-capture'];

export interface DictationHandlers {
    /** the new full text of the question box */
    onText(text: string): void;
    /** dictation ended (stopped, finished, failed); `error` = a message to show, if any */
    onEnd(error?: string): void;
}

/** One dictation: `start(base)` listens and reports the merged text; `stop()` ends it, keeping what was heard. */
export class AssistantDictation {
    private recognition: SpeechRecognitionLike | null = null;
    private confirmed = '';
    private base = '';
    private error = '';
    private startToken = 0;
    private waiting = false;

    constructor(
        private readonly win: any,
        private readonly handlers: DictationHandlers
    ) {}

    get listening(): boolean {
        return !!this.recognition;
    }

    /** Starts listening after the text already in the box; false when the browser has no speech recognition or it will not start. */
    start(base: string): boolean {
        if (this.recognition) {
            return false;
        }
        this.base = base || '';
        this.confirmed = '';
        return this.begin(true);
    }

    /**
     * `local` = ask the browser to recognise on the device (processLocally). When that fails for any reason other than the
     * microphone itself, the dictation is retried once with the browser default (online). Chrome and Edge report the failure with
     * `onerror` and may never fire `onend` afterwards, so the retry and the final message do not wait for `onend`.
     */
    private begin(local: boolean): boolean {
        const Ctor = speechRecognitionCtor(this.win);
        if (!Ctor || this.recognition) {
            return false;
        }
        let recognition: SpeechRecognitionLike;
        try {
            recognition = new Ctor();
            recognition.lang = voiceLang(this.win.navigator);
            recognition.continuous = true;
            recognition.interimResults = true;
            if (local && 'processLocally' in recognition) {
                recognition.processLocally = true;
            }
            let done = false;
            let timer: any = null;
            let error = '';
            const finish = (retryOnline: boolean) => {
                if (done) {
                    return;
                }
                done = true;
                if (timer !== null) {
                    (this.win.clearTimeout || clearTimeout).call(this.win, timer);
                }
                recognition.onresult = recognition.onerror = recognition.onend = null;
                if (this.recognition === recognition) {
                    this.recognition = null;
                }
                try {
                    recognition.abort(); // also when onend never came: the session may still hold the microphone
                } catch (e) {
                    /* already ended */
                }
                if (retryOnline) {
                    if (this.begin(false)) {
                        return;
                    }
                    error = error || 'Voice input could not start. Type the question instead.';
                }
                this.handlers.onEnd(error || undefined);
            };
            this.error = '';
            recognition.onresult = (event) => this.onResult(event);
            recognition.onerror = (event) => {
                const code = event && event.error;
                if (local && NO_ONLINE_RETRY.indexOf(code || '') < 0 && !!code) {
                    error = voiceErrorText(code);
                    finish(true);
                    return;
                }
                error = voiceErrorText(code);
                this.error = error;
                // some browsers never fire onend after an error: end the session ourselves
                timer = (this.win.setTimeout || setTimeout).call(this.win, () => finish(false), 1500);
            };
            recognition.onend = () => finish(false);
            this.recognition = recognition;
            recognition.start();
            return true;
        } catch (e) {
            this.recognition = null;
            return false;
        }
    }

    /** True between `startAfterMicrophone` and the answer to the microphone prompt. */
    get pending(): boolean {
        return this.waiting;
    }

    /**
     * Asks for the microphone (`ask` returns '' when allowed, else a message), then starts listening. null = `stop()` came
     * first, nothing started; `error` = a message to show; `started` = whether listening began.
     */
    async startAfterMicrophone(base: string, ask: () => Promise<string>): Promise<{started: boolean; error?: string} | null> {
        if (this.recognition || this.waiting) {
            return {started: false};
        }
        const token = ++this.startToken;
        this.waiting = true;
        const denied = await ask();
        if (token !== this.startToken) {
            return null;
        }
        this.waiting = false;
        if (denied) {
            return {started: false, error: denied};
        }
        const started = this.start(base);
        return started ? {started: true} : {started: false, error: 'Voice input could not start. Type the question instead.'};
    }

    stop(): void {
        this.startToken++; // a start still waiting for the microphone prompt is cancelled
        this.waiting = false;
        try {
            this.recognition?.stop();
        } catch (e) {
            this.recognition = null;
        }
    }

    private onResult(event: any): void {
        let interim = '';
        const results = event && event.results ? event.results : [];
        for (let i = event && typeof event.resultIndex === 'number' ? event.resultIndex : 0; i < results.length; i++) {
            const text = results[i] && results[i][0] ? String(results[i][0].transcript || '') : '';
            if (results[i].isFinal) {
                this.confirmed = `${this.confirmed} ${text}`.trim();
            } else {
                interim += text;
            }
        }
        this.handlers.onText(mergeDictation(this.base, this.confirmed, interim));
    }
}

/**
 * Asks for the microphone once, before listening, so the browser shows its permission prompt (Edge needs it for the speech
 * service). The stream is closed at once: no audio is read, kept or sent. '' = allowed (or the browser cannot ask).
 */
export async function ensureMicrophone(nav: any): Promise<string> {
    const media = nav && nav.mediaDevices;
    if (!media || typeof media.getUserMedia !== 'function') {
        return '';
    }
    try {
        const stream = await media.getUserMedia({audio: true});
        stream?.getTracks?.().forEach((track: any) => track.stop());
        return '';
    } catch (e) {
        const name = e && (e as any).name;
        if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
            return voiceErrorText('audio-capture');
        }
        if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
            return voiceErrorText('not-allowed');
        }
        return ''; // busy or unknown: let the recognition try and report its own error
    }
}
